import { DEFAULT_PLATFORM_BUSINESS_DEFAULTS, type PlatformBusinessDefaults } from './resolveOrgSettings.js';

/** Legacy Tools & Bubbles placeholder domains (tb-site). */
export const TB_GUEST_PLACEHOLDER_DOMAIN = 'guest.toolsandbubbles.in';
export const TB_PHONE_PLACEHOLDER_DOMAIN = 'phone.toolsandbubbles.com';
export const TB_LEGACY_GUEST_DOMAIN = 'toolsandbubbles.com';

export interface PlaceholderEmailDomains {
  guest: string;
  phone: string;
}

export function resolvePlaceholderEmailDomains(
  platform?: Partial<PlatformBusinessDefaults> | null,
): PlaceholderEmailDomains {
  const defaults = DEFAULT_PLATFORM_BUSINESS_DEFAULTS;
  return {
    guest: platform?.placeholderGuestEmailDomain ?? defaults.placeholderGuestEmailDomain,
    phone: platform?.placeholderPhoneEmailDomain ?? defaults.placeholderPhoneEmailDomain,
  };
}

function sanitizeLocalPart(value: string): string {
  const cleaned = value.replace(/\D/g, '') || value.replace(/[^a-zA-Z0-9._-]/g, '');
  return cleaned || `guest_${Date.now()}`;
}

export function buildGuestPlaceholderEmail(seed: string, domains?: PlaceholderEmailDomains): string {
  const { guest } = domains ?? resolvePlaceholderEmailDomains();
  const local = sanitizeLocalPart(seed);
  return `guest_${local}@${guest}`;
}

export function buildPhonePlaceholderEmail(phone: string, domains?: PlaceholderEmailDomains): string {
  const { phone: domain } = domains ?? resolvePlaceholderEmailDomains();
  const digits = phone.replace(/\D/g, '') || `customer_${Date.now()}`;
  return `${digits}@${domain}`;
}

/** T&B tb-site placeholder domains (not platform-configurable). */
export function resolveTbPlaceholderEmailDomains(): PlaceholderEmailDomains {
  return {
    guest: TB_GUEST_PLACEHOLDER_DOMAIN,
    phone: TB_PHONE_PLACEHOLDER_DOMAIN,
  };
}

/** T&B admin booking legacy guest email (`guest_{ts}@toolsandbubbles.com`). */
export function buildTbLegacyAdminGuestEmail(seed?: string | number): string {
  const local = seed ?? Date.now();
  return `guest_${local}@${TB_LEGACY_GUEST_DOMAIN}`;
}

/** Phone-only profile guest email on tb-site (`{digits}@guest.toolsandbubbles.in`). */
export function buildTbProfileGuestEmail(phone: string): string {
  const digits = phone.replace(/\D/g, '') || `customer_${Date.now()}`;
  return `${digits}@${TB_GUEST_PLACEHOLDER_DOMAIN}`;
}

/** Validate hostname for placeholder auth email domains (no @ prefix). */
export function isValidPlaceholderEmailDomain(domain: string): boolean {
  const trimmed = domain.trim().toLowerCase();
  if (!trimmed || trimmed.includes('@') || trimmed.includes(' ')) return false;
  const hostnameRe = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
  return hostnameRe.test(trimmed);
}
